import { Box, Chip, Paper, Typography } from "@mui/material"
import type { FC } from "react"
import { twMerge } from "tailwind-merge"

import { useGoodsReceiptContext } from "../../contexts"
import { getGoodsReceiptContainerTrackingNumbers } from "../../utils"

import { GoodReceiptDetailsStatusDate } from "./GoodReceiptDetailsStatusDate"
import { GoodsReceiptDetailsHeaderActions } from "./GoodsReceiptDetailsHeaderActions"
import { GoodsReceiptDetailsInfoItem } from "./GoodsReceiptDetailsInfoItem"
import { GoodsReceiptStatusChip } from "./GoodsReceiptStatusChip"

import { Button } from "@/components/common/Button"
import { ValueOrMultiple } from "@/components/common/ValueOrMultiple"
import { PermissionActionType } from "@/constants/permissions"
import { type GoodsReceipt, GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface GoodsReceiptDetailsHeaderProps {
  goodsReceipt: GoodsReceipt
  palletsCount: number | null
  className?: string
}

export const GoodsReceiptDetailsHeader: FC<GoodsReceiptDetailsHeaderProps> = ({
  goodsReceipt,
  palletsCount,
  className,
}) => {
  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])
  const { onEdit, onUpload, onDelete, onAddPurchaseOrder, onMarkAsNotReceived, onCreatePallets } =
    useGoodsReceiptContext()
  const isIncoming = goodsReceipt.status === GoodsReceiptStatus.Incoming
  const isStored = goodsReceipt.status === GoodsReceiptStatus.Stored
  const isCheckedIn = goodsReceipt.status === GoodsReceiptStatus.CheckedIn
  const shouldShowAddPurchaseOrder = !isCheckedIn && !isStored

  return (
    <Paper
      elevation={0}
      variant='outlined'
      className={twMerge("flex flex-col gap-6 rounded-md border-gray-400 p-6", className)}
    >
      <Box className='flex items-center gap-x-4'>
        <Typography variant='h5' component='h2' className='text-gray-800'>
          {isIncoming ? "Advanced Goods Receipt" : "Goods Receipt"}:{" "}
          <Box component='span' data-testid='goods-receipt-id'>
            {goodsReceipt.agrNumber}
          </Box>
        </Typography>
        <GoodsReceiptStatusChip status={goodsReceipt.status} className='mr-auto' data-testid='goods-receipt-status' />

        {canMaintain && (
          <>
            {shouldShowAddPurchaseOrder && onAddPurchaseOrder && (
              <Button appearance='outlined' data-testid='add-po-button' onClick={() => onAddPurchaseOrder()}>
                Add PO
              </Button>
            )}

            <GoodsReceiptDetailsHeaderActions
              goodsReceipt={goodsReceipt}
              onEdit={onEdit}
              onCreatePallets={onCreatePallets}
              onUpload={onUpload}
              onDelete={onDelete}
              onMarkAsNotReceived={onMarkAsNotReceived}
            />
          </>
        )}
      </Box>

      <Box className='flex gap-x-3'>
        <GoodsReceiptDetailsInfoItem label='Created by:'>
          <Chip className='h-6 rounded text-xs' label={goodsReceipt.createdByUser?.name} data-testid='created-by' />
        </GoodsReceiptDetailsInfoItem>

        {!isIncoming && (
          <GoodsReceiptDetailsInfoItem label='Received by:'>
            <Chip
              className='h-6 rounded text-xs'
              label={goodsReceipt?.softReceiveData?.receivedByUser?.name ?? "-"}
              data-testid='received-by'
            />
          </GoodsReceiptDetailsInfoItem>
        )}

        <GoodsReceiptDetailsInfoItem label={isIncoming ? "Est. Total Pallets:" : "Total Pallets:"}>
          <Chip
            label={palletsCount ?? "-"}
            className='h-6 rounded bg-primary-200 text-xs'
            data-testid='estimated-pallets-count'
          />
        </GoodsReceiptDetailsInfoItem>
        <GoodReceiptDetailsStatusDate goodsReceipt={goodsReceipt} />
      </Box>

      <Box className='flex gap-x-6'>
        <GoodsReceiptDetailsInfoItem label='Container/Tracking #:'>
          <ValueOrMultiple
            values={getGoodsReceiptContainerTrackingNumbers(goodsReceipt)}
            emptyValue='None'
            className='font-medium'
            data-testid='tracking-numbers'
          />
        </GoodsReceiptDetailsInfoItem>

        {isIncoming && (
          <GoodsReceiptDetailsInfoItem label={"Delivery Location:"}>
            <Box component='span' className='font-medium' data-testid='warehouse'>
              {formatWarehouseLabel(goodsReceipt.warehouseArea!)}
            </Box>
          </GoodsReceiptDetailsInfoItem>
        )}

        <GoodsReceiptDetailsInfoItem label={"Current Location:"}>
          <Box component='span' className='font-medium' data-testid='warehouse'>
            {isIncoming ? "In Transit" : formatWarehouseLabel(goodsReceipt.warehouseArea!)}
          </Box>
        </GoodsReceiptDetailsInfoItem>
      </Box>
    </Paper>
  )
}
